import { useEffect, useMemo, useState } from "react";
import StatementTab, { countMatchingRows, type RowFilter } from "./StatementTab";
import { normaliseNeedle, rowMatchesText } from "../lib/textSearch";
import { PERIOD_IN } from "../lib/columns";
import { useScrollEdges } from "../lib/scrollEdges";
import SideBySideTab from "./SideBySideTab";
import OtherTab from "./OtherTab";
import ClearedSubsequentTab, { clearedTabCount } from "./ClearedSubsequentTab";
import ManualMatchTab from "./ManualMatchTab";
import ManualNotesTab from "./ManualNotesTab";
import AutoMatchedTab from "./AutoMatchedTab";
import UnreconciledTab from "./UnreconciledTab";
import type { ReconResult, ManualMatchPair, OtherSections, FrameRecords } from "../types/api";

interface Props {
  result: ReconResult;
  // The pre-manual-match frames from /reconcile; ManualMatchTab patches the finalize
  // frame-delta onto these (see lib/framesDelta.ts).
  baseline: ReconResult;
  prematchId: string;
  insurerName: string;
  // The sidebar's period window (ISO yyyy-mm-dd) for the CURRENTLY displayed result.
  // Seeds the From/To filters below so this view opens on the period being
  // reconciled; the user can still narrow it per-tab without touching the recon.
  periodFromSetting?: string;
  periodToSetting?: string;
  // The Matched tolerance the current result was computed with. Passed down so the
  // prior-period tie preview gates on the SAME number the engine will.
  amountTol: number;
  sessionPairs: ManualMatchPair[];
  notesCri: Record<string, string>;
  notesInsurer: Record<string, string>;
  bridgeRejections: string[];
  rebridging: boolean;
  onResultUpdate: (result: ReconResult, pairs: ManualMatchPair[], full?: boolean) => void;
  onNotesUpdate: (result: ReconResult | null, notesCri: Record<string, string>, notesInsurer: Record<string, string>) => void;
  onBridgeReject: (next: string[]) => void;
  // The server's saved session for this result expired: re-run it and resolve the new
  // prematch id, so a manual match or note can retry instead of dead-ending.
  onPrematchExpired: () => Promise<string>;
  // A carried-item pair was saved. Pairs live server-side and only reach the figures
  // through the engine, so this asks the page to re-run rather than patching totals
  // client-side. Optional: the tab degrades to "saved, refresh to see it" without it.
  // Awaited, so the Manual Match bar stays busy until the re-run lands.
  onCarriedPairsChanged?: () => Promise<void> | void;
  // The transaction rows arrive separately from the figures (see ResultPage). Every tab
  // below renders rows, so the section stays shut until they land — an open statement
  // view over the empty stand-in would read as "this ledger has no transactions".
  framesReady: boolean;
  // Open state is OWNED BY ResultPage: opening this is what makes the rows necessary, so
  // it both triggers their fetch and tells a later re-run to ask for them inline.
  shown: boolean;
  onShow: () => void;
}

// Additional Details — a date-range + all-column search filter row, then the tab strip.
// Per-column filtering lives in the grid headers (DataTable's HeaderFilter), so this
// row carries only what a header checklist cannot express. Lazy-loaded behind a "Show"
// gate like the Streamlit expander.

export default function AdditionalDetails({ result, baseline, prematchId, insurerName, periodFromSetting, periodToSetting, amountTol, sessionPairs, notesCri, notesInsurer, bridgeRejections, rebridging, onResultUpdate, onNotesUpdate, onBridgeReject, onPrematchExpired, onCarriedPairsChanged, framesReady, shown, onShow }: Props) {
  const [tab, setTab] = useState(0);

  const cri = result.df_cri;
  const insurer = result.df_insurer;

  // Min/max doc_date across both frames — the From/To date filters default to the
  // full span (like the Streamlit date_input defaults of min_d / max_d).
  const dateSpan = useMemo(() => {
    let min = "", max = "";
    for (const frame of [cri, insurer]) {
      const i = frame.columns.indexOf("doc_date");
      if (i < 0) continue;
      for (const r of frame.rows) {
        const s = String(r[i] ?? "").slice(0, 10);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) continue;
        if (!min || s < min) min = s;
        if (!max || s > max) max = s;
      }
    }
    return { min, max };
  }, [cri, insurer]);

  // The From/To the filter row should show when the user has not overridden it: the
  // sidebar's period window, so this view opens on the period actually being
  // reconciled. Either bound may be blank (the period is auto-derived far more often
  // than it is set), and then that side falls back to the data's own span — a blank
  // bound would otherwise read as "no limit" and silently disagree with the sidebar.
  const defaultFrom = periodFromSetting || dateSpan.min;
  const defaultTo = periodToSetting || dateSpan.max;

  const [filters, setFilters] = useState<RowFilter>({
    categories: [], statuses: [], criTypes: [], insurerTypes: [], lob: [],
    // Reconciled period only, by design: post-period rows play no part in the recon.
    // They stay in the frame (backend/summary.py::_apply_period_filter); pre-period and
    // undated ones are listed on the Other tab.
    periods: [PERIOD_IN],
    dateFrom: defaultFrom, dateTo: defaultTo, text: "",
    spanFrom: dateSpan.min, spanTo: dateSpan.max,
  });

  // Has the user edited the date filter by hand? Once they have, the sidebar stops
  // driving it — re-syncing would silently discard their narrowing on the next
  // re-render. Reset when the sidebar period itself moves (below), because that is a
  // deliberate new intent that should win.
  const [dateTouched, setDateTouched] = useState(false);

  // Follow the sidebar period, and the data span it falls back to. A re-run
  // (tolerance/period change) moves both, and stale bounds from the previous result
  // must not linger and hide rows. Skipped once the user has taken the filter over.
  // spanFrom/spanTo always track the DATA (never the sidebar): they exist so the tabs
  // can tell a defaulted bound from a narrowed one, and a period-seeded bound tighter
  // than the data span is a genuine narrowing that must exclude undated rows.
  useEffect(() => {
    setDateTouched(false);
  }, [periodFromSetting, periodToSetting]);

  useEffect(() => {
    setFilters((f) => ({
      ...f,
      ...(dateTouched ? {} : { dateFrom: defaultFrom, dateTo: defaultTo }),
      spanFrom: dateSpan.min, spanTo: dateSpan.max,
    }));
  }, [defaultFrom, defaultTo, dateSpan.min, dateSpan.max, dateTouched]);

  // The window's From bound, for the Other tab's before/after split. Surfaced by the
  // engine as period_from (the bound the CALLER set) — distinct from period_start,
  // which is the derived data floor and would mislabel rows.
  const periodFrom = useMemo(() => {
    const v = result.period_from;
    return typeof v === "string" ? v.slice(0, 10) : null;
  }, [result.period_from]);

  // Other tab count: exactly the rows the tab LISTS, so the badge never promises rows
  // the tab omits. That means insurer "Other" + "Claim" rows and out-of-window rows on
  // either side, MINUS the post-period rows (excluded by design — they live on the
  // statement tabs). Counted here rather than in OtherTab so the tab strip renders
  // without mounting the tab.
  const otherN = useMemo(() => {
    const count = (f: typeof insurer, isCri: boolean) => {
      const cCat = f.columns.indexOf("recon_category");
      const cWin = f.columns.indexOf("in_period_window");
      const cDate = f.columns.indexOf("doc_date");
      let n = 0;
      for (const r of f.rows) {
        const inWin = cWin >= 0 ? r[cWin] !== false : true;
        if (!inWin) {
          const d = cDate >= 0 ? String(r[cDate] ?? "").slice(0, 10) : "";
          // Undated or before the From bound => listed; at/after it => omitted.
          if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || (periodFrom && d < periodFrom)) n++;
          continue;
        }
        const cat = cCat >= 0 ? String(r[cCat] ?? "") : "";
        if (cat === "Other" || (!isCri && cat === "Claim")) { n++; continue; }
        // An in-window UNDATED row is listed by the "Undated transactions" reference
        // block even when its category is a real bucket (Daman's Missing-Invoice
        // rows), so it must be counted or the badge would under-promise.
        const d2 = cDate >= 0 ? String(r[cDate] ?? "").slice(0, 10) : "";
        if (!/^\d{4}-\d{2}-\d{2}$/.test(d2)) n++;
      }
      return n;
    };
    // Plus the previous output's carried Others and the prior-period claims, each listed
    // in its own section.
    return count(insurer, false) + count(cri, true) + (result.prior_others?.count ?? 0)
      + (result.unreconciled?.claims_count ?? 0);
  }, [insurer, cri, periodFrom, result.prior_others, result.unreconciled]);

  // The prior-period list's still-open claims, under the columns finance's own file
  // carried (as the Prior-Period tab shows them).
  const priorClaims = useMemo(() => {
    const u = result.unreconciled;
    const f = u?.rows;
    const ci = f ? f.columns.indexOf("unrec_claim") : -1;
    if (!f || ci < 0) return null;
    const pos = f.rows.flatMap((r, i) => (r[ci] === true ? [i] : []));
    if (!pos.length) return null;
    const keep = (u?.source_columns?.length ? u.source_columns : f.columns)
      .filter((c) => f.columns.includes(c));
    const at = keep.map((c) => f.columns.indexOf(c));
    return {
      columns: keep,
      rows: pos.map((p) => at.map((j) => f.rows[p][j])),
      indices: pos.map((p) => f.indices[p]),
      total_rows: pos.length,
    };
  }, [result.unreconciled]);

  // Per-tab match counts, shown only while a search is active. Without them the
  // search silently answered "no rows" on the tab you happened to be on, with no way
  // to tell "this row is on another tab" from "this row does not exist" — the tab
  // strip carries no search feedback and every tab's empty state reads the same.
  // Counted from the SAME predicate the tabs filter with, so a count can never
  // promise rows the tab then fails to show. Memoised on (frames, filters); a
  // Dubai-sized pair of frames costs ~12ms and only recomputes when the query moves.
  const searchActive = normaliseNeedle(filters.text).length > 0;
  // Prior-period items are NOT in the two engine frames, so they need their own count
  // or a search that only hits a carried item reports "no row matches on any
  // statement" while the Prior-Period tab is holding the match. Counted over the
  // file's own columns (source_columns), which is exactly what that tab renders.
  // BOTH lists: counting only the insurer list made a search for a CRI-book carried
  // item report "No row matches ... on any statement".
  const unrecMatchCount = useMemo(() => {
    const blk = result.unreconciled;
    const needle = normaliseNeedle(filters.text);
    if (!blk || !needle) return 0;
    let n = 0;
    for (const [f, src] of [[blk.rows, blk.source_columns],
                            [blk.cri_rows, blk.cri_source_columns]] as const) {
      if (!f) continue;
      const keep = (src ?? f.columns)
        .map((c) => f.columns.indexOf(c)).filter((i) => i >= 0);
      const cols = keep.map((i) => f.columns[i]);
      const shape: FrameRecords = { columns: cols, rows: [], indices: [], total_rows: 0 };
      for (const r of f.rows) {
        if (rowMatchesText(shape, keep.map((i) => r[i]), needle)) n++;
      }
    }
    return n;
  }, [result.unreconciled, filters.text]);

  const matchCounts = useMemo(() => {
    if (!searchActive) return null;
    const nCri = countMatchingRows(cri, filters);
    const nIns = countMatchingRows(insurer, filters);
    return {
      cri: nCri, insurer: nIns, sideBySide: nCri + nIns,
      prior: unrecMatchCount,
      total: nCri + nIns + unrecMatchCount,
    };
  }, [searchActive, cri, insurer, filters, unrecMatchCount]);

  const set = (patch: Partial<RowFilter>) => setFilters((f) => ({ ...f, ...patch }));

  // DEBOUNCED. This one box drives matchCounts — a full pass over BOTH frames — plus the
  // active tab's own filter and its re-render: measured ~265ms per keystroke on Dubai, so
  // typing a six-character reference cost ~1.6s of blocked main thread. The input stays
  // fully controlled off `textInput` (typing is instant); only the COMMITTED value is
  // delayed, so a burst of keystrokes coalesces into one pass. Same shape as the sidebar
  // recompute's debounce in ResultPage.
  const [textInput, setTextInput] = useState("");
  useEffect(() => {
    if (textInput === filters.text) return;
    const t = setTimeout(() => set({ text: textInput }), 180);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textInput, filters.text]);
  // Clearing has to move BOTH, or the effect above would immediately re-commit the text
  // still sitting in the box — which is what the Prior-Period tab's "clear search" does.
  const clearText = () => { setTextInput(""); set({ text: "" }); };

  // ABOVE the collapsed-state early return below: this component renders a placeholder
  // until the rows arrive, so a hook called after that return would change the hook
  // count on expansion (React #310, blank screen).
  const stripRef = useScrollEdges();

  if (!shown || !framesReady) {
    return (
      <div className="card" style={{ marginTop: 16 }}>
        <p className="muted" style={{ margin: "0 0 10px" }}>
          Full CRI / Insurer statements, filters, side-by-side and manual overrides load on demand
          (large tables) — including <strong>Manual Match</strong> (pair rows the app couldn't
          auto-match) and <strong>Manual Notes</strong> (annotate rows for the export). The
          complete data is also in the Annotated Workbook export below.
        </p>
        {/* Clicking before the rows have arrived starts their fetch and waits here rather
            than opening onto an empty table. The figures above are already final. */}
        <button className="btn" onClick={onShow} disabled={shown && !framesReady}>
          {shown && !framesReady
            ? <><span className="spinner" /> Loading transactions…</>
            : "Show Full Statement View & Filters"}
        </button>
      </div>
    );
  }

  // The Auto-Matched tab appears ONLY when the engine produced bridge pairs (name+amount
  // auto-matches), matching the Streamlit conditional — most insurers have none.
  const bridgeApplied = (result.bridge_applied as unknown[] | undefined) ?? [];
  const bridgeRejected = (result.bridge_rejected as unknown[] | undefined) ?? [];
  const hasBridge = bridgeApplied.length > 0 || bridgeRejected.length > 0;

  // Cleared-in-Subsequent-Month appears ONLY when the pass found something (it is
  // opt-in per insurer), mirroring the Auto-Matched conditional above.
  const clearedCount = clearedTabCount(result);
  const hasCleared = clearedCount > 0;

  // While a search is active the row-listing tabs show THEIR match count, so the
  // matches can be found without opening each tab in turn. The tabs that do not
  // render the filtered frames (Manual Notes/Match, Auto-Matched, Cleared) keep their
  // static labels — a search count there would be a promise the tab does not keep.
  // {label, count} rather than a count baked into the label: the count renders as a
  // pill so a tab holding nothing visibly recedes. Indices are unchanged — every
  // `tab === n` check below still refers to the same view.
  const tabs: { label: string; count?: number }[] = [
    { label: "CRI Statement", count: matchCounts?.cri },
    { label: "Insurer Statement", count: matchCounts?.insurer },
    { label: "Side by Side", count: matchCounts?.sideBySide },
    { label: "Other", count: otherN || undefined },
    { label: "Manual Notes" }, { label: "Manual Match" },
  ];
  if (hasBridge) tabs.push({ label: "Auto-Matched", count: bridgeApplied.length });
  if (hasCleared) tabs.push({ label: "Cleared in Subsequent Month", count: clearedCount });
  const clearedTabIdx = hasBridge ? 7 : 6;
  // Prior-period unreconciled items (GL, opt-in file). Appended LAST so every index
  // above is unchanged; absent entirely when the feature is unused. Every item that
  // the adjusted closing counts is listed here — nothing is footed but unlisted.
  const unrec = result.unreconciled ?? null;
  const unrecTabIdx = tabs.length;
  // While a search is active the label carries ITS match count, like the row-listing
  // tabs above — otherwise a search whose only hit is a carried item gives no clue
  // which tab to open.
  if (unrec) {
    tabs.push({
      label: "Prior-Period Unreconciled",
      // Both lists, so a CRI-only upload does not read as an empty tab.
      count: matchCounts ? matchCounts.prior
        : unrec.open_count + unrec.matched_count
          + (unrec.cri_open_count ?? 0) + (unrec.cri_matched_count ?? 0),
    });
  }

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3 style={{ marginTop: 0 }}>Additional Details — Filters &amp; Full Statement View</h3>

      {/* Filter row. Only the two filters the per-column header filters CANNOT express:
          a date RANGE (header filters are distinct-value checklists) and a search that
          spans every column at once. Category / Match Status / CRI Type / Insurer Type /
          Line of Business all had exact per-column equivalents in the grid headers
          (pinnable, and scoped to the table being read), so they were removed as
          duplicates rather than kept in two places that could disagree. */}
      <div className="filter-row">
        <div className="filter-cell">
          <label className="field-label" style={{ marginTop: 0 }} htmlFor="filter-date-from">From Date</label>
          <input id="filter-date-from" className="input" type="date" value={filters.dateFrom}
            onChange={(e) => { setDateTouched(true); set({ dateFrom: e.target.value }); }} />
        </div>
        <div className="filter-cell">
          <label className="field-label" style={{ marginTop: 0 }} htmlFor="filter-date-to">To Date</label>
          <input id="filter-date-to" className="input" type="date" value={filters.dateTo}
            onChange={(e) => { setDateTouched(true); set({ dateTo: e.target.value }); }} />
        </div>
        <div className="filter-cell">
          <label className="field-label" style={{ marginTop: 0 }} htmlFor="filter-text">Text Search</label>
          <input id="filter-text" className="input" placeholder="Search across all columns…" value={textInput} onChange={(e) => setTextInput(e.target.value)} />
        </div>
      </div>

      {/* Say where the From/To came from. The dates default to the sidebar's period so
          this view opens on the period being reconciled; once overridden they stop
          following it, and the reset puts them back. Without this the two date pairs
          on screen look like they should agree and give no clue why they don't. */}
      {(periodFromSetting || periodToSetting) && (
        <p className="muted" style={{ margin: "-4px 0 12px", fontSize: "0.82rem" }}>
          {dateTouched
            ? "Dates overridden here — the sidebar's reconciliation period is unchanged."
            : "Dates follow the sidebar's reconciliation period. Changing them filters this view only."}
          {dateTouched && (
            <button className="btn" style={{ fontSize: "0.78rem", padding: "3px 8px", marginLeft: 8 }}
              onClick={() => { setDateTouched(false); set({ dateFrom: defaultFrom, dateTo: defaultTo }); }}>
              Reset to period
            </button>
          )}
        </p>
      )}

      {/* Where the matches are. Without this a search that hits only the other side
          left the current tab reading "No items to display" — indistinguishable from
          "this transaction does not exist anywhere". */}
      {matchCounts && (
        <p className="muted" style={{ margin: "-4px 0 12px", fontSize: "0.82rem" }} role="status">
          {matchCounts.total === 0
            ? `No row matches "${filters.text.trim()}" on any statement.`
            : `"${filters.text.trim()}" matches ${matchCounts.total.toLocaleString()} `
              + `row${matchCounts.total === 1 ? "" : "s"} — `
              + `CRI ${matchCounts.cri.toLocaleString()}, `
              + `Insurer ${matchCounts.insurer.toLocaleString()}`
              + (unrec ? `, Prior-period ${matchCounts.prior.toLocaleString()}` : "")
              + `. Counts are per tab in the strip below.`}
        </p>
      )}

      {/* Tab strip. Real tablist semantics: only the ACTIVE tab is tabbable (roving
          tabindex), so Tab enters/leaves the strip in one step and Left/Right move
          between tabs — the WAI-ARIA pattern screen-reader users expect. */}
      <div className="tabstrip" role="tablist" aria-label="Statement views" ref={stripRef}>
        {tabs.map((t, i) => (
          <button
            key={t.label}
            type="button"
            role="tab"
            id={`tab-${i}`}
            aria-selected={tab === i}
            aria-controls={`tabpanel-${i}`}
            tabIndex={tab === i ? 0 : -1}
            className={`tab ${tab === i ? "active" : ""}`}
            onClick={() => setTab(i)}
            onKeyDown={(e) => {
              if (e.key !== "ArrowRight" && e.key !== "ArrowLeft" &&
                  e.key !== "Home" && e.key !== "End") return;
              e.preventDefault();
              const next =
                e.key === "Home" ? 0
                : e.key === "End" ? tabs.length - 1
                : e.key === "ArrowRight" ? (i + 1) % tabs.length
                : (i - 1 + tabs.length) % tabs.length;
              setTab(next);
              document.getElementById(`tab-${next}`)?.focus();
            }}
          >
            {t.label}
            {t.count !== undefined && (
              <span className={`tab-count ${t.count === 0 ? "tab-count--zero" : ""}`}>
                {t.count.toLocaleString()}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* One panel wrapper for whichever tab is active — its id/labelling tracks the
          selected tab, so aria-controls resolves without touching each tab body. */}
      <div role="tabpanel" id={`tabpanel-${tab}`} aria-labelledby={`tab-${tab}`}>
      {tab === 0 && <StatementTab frame={cri} isCri filters={filters} config={result.display_config?.cri} flat insurerName={insurerName} />}
      {tab === 1 && <StatementTab frame={insurer} isCri={false} filters={filters} config={result.display_config?.insurer} flat insurerName={insurerName} />}
      {tab === 2 && (
        <SideBySideTab cri={cri} insurer={insurer} filters={filters}
          criConfig={result.display_config?.cri} insConfig={result.display_config?.insurer}
          insurerName={insurerName} evidenceVocabulary={result.match_evidence_legend} />
      )}
      {tab === 3 && (
        <OtherTab frame={insurer} config={result.display_config?.insurer}
          criFrame={cri} criConfig={result.display_config?.cri}
          sections={result.other_sections as OtherSections | undefined}
          periodFrom={periodFrom} insurerName={insurerName}
          priorOthers={result.prior_others} priorClaims={priorClaims}
          priorClaimsTotal={result.unreconciled?.claims_total ?? 0} />
      )}
      {tab === 4 && (
        <ManualNotesTab result={result} prematchId={prematchId} insurerName={insurerName}
          sessionPairs={sessionPairs} notesCri={notesCri} notesInsurer={notesInsurer} onApplied={onNotesUpdate}
          onPrematchExpired={onPrematchExpired} />
      )}
      {tab === 5 && (
        <ManualMatchTab result={result} baseline={baseline} prematchId={prematchId} insurerName={insurerName}
          sessionPairs={sessionPairs} onApplied={onResultUpdate} amountTol={amountTol}
          onPrematchExpired={onPrematchExpired} onCarriedPairsChanged={onCarriedPairsChanged} />
      )}
      {tab === 6 && hasBridge && (
        <AutoMatchedTab result={result} bridgeRejections={bridgeRejections}
          rebridging={rebridging} onBridgeReject={onBridgeReject} insurerName={insurerName} />
      )}
      {tab === clearedTabIdx && hasCleared && <ClearedSubsequentTab result={result}
        insurerName={insurerName} onChanged={onCarriedPairsChanged} />}
      {tab === unrecTabIdx && unrec && (
        <UnreconciledTab block={unrec} insurerName={insurerName}
          criFrame={cri} insurerFrame={insurer}
          criConfig={result.display_config?.cri} insConfig={result.display_config?.insurer}
          // The shared Text Search reaches this tab too. Only the text: the From/To
          // dates filter this period's doc_date, which a prior-period item has no
          // equivalent of, so applying them here would hide every row.
          searchText={filters.text}
          onClearSearch={clearText}
          onPairsChanged={onCarriedPairsChanged}
          // Prior-period items are matched in the Manual Match tab (finance 2026-10-08).
          onOpenManualMatch={() => setTab(5)} />
      )}
      </div>
    </div>
  );
}
